/**
 * KPI/stat card for dashboards. Shows a title, a prominent value, an optional
 * icon, and an optional delta (fraction, e.g. 0.12 => "+12%") coloured by sign.
 */
import type { ReactElement, ReactNode } from 'react';
import { formatPercent } from '@/utils/formatters';

export interface StatCardProps {
  title: string;
  value: ReactNode;
  /** Change as a fraction: 0.12 => +12%, -0.05 => -5%. */
  delta?: number;
  icon?: ReactNode;
  /** Label describing the delta period, e.g. "so với hôm qua". */
  deltaLabel?: string;
  className?: string;
}

/** Summary metric tile. */
export default function StatCard({
  title,
  value,
  delta,
  icon,
  deltaLabel,
  className = '',
}: StatCardProps): ReactElement {
  const hasDelta = typeof delta === 'number' && Number.isFinite(delta);
  const isUp = hasDelta && (delta as number) >= 0;

  return (
    <div className={`card flex items-start justify-between gap-4 ${className}`.trim()}>
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-gray-500">{title}</p>
        <p className="mt-1 text-2xl font-semibold text-gray-900">{value}</p>
        {hasDelta && (
          <p
            className={`mt-1 flex items-center gap-1 text-xs font-medium ${
              isUp ? 'text-green-600' : 'text-red-600'
            }`}
          >
            <span aria-hidden="true">{isUp ? '▲' : '▼'}</span>
            <span>{formatPercent(Math.abs(delta as number))}</span>
            {deltaLabel && <span className="font-normal text-gray-400">{deltaLabel}</span>}
          </p>
        )}
      </div>
      {icon && (
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brand-600">
          {icon}
        </div>
      )}
    </div>
  );
}
